import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Col,
  Row,
  Modal,
  Form,
  Input,
  Spin,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import { postHistorialDeTramite } from 'api/tramites/historial';
import Dragger from 'components/Dragger';
import { patchTramite } from 'api/tramites/tramites';
import { CheckCircleOutlined } from '@ant-design/icons';
import { stepsEnum, tiposDePasosEnum } from '../enums';

export const generateValues = (pasoActual, pasos = [], puede_continuar = false) => {
  if (!pasoActual) {
    return [];
  }
  const pasoSiguiente = pasos.find((p) => p.orden === pasoActual?.orden + 1)
    || pasos.find((p) => p.orden === pasoActual?.orden + 2);
  const formValues = {
    paso: pasoActual.id,
    paso_a_mandar: puede_continuar ? pasoSiguiente?.id || pasoActual?.id : pasoActual?.id,
  };
  return [formValues, pasoSiguiente];
};

const pasosInicialesPuedeReatender = [
  stepsEnum.REQUISITOS_DEL_CASO,
  stepsEnum.REQUISITOS_GENERALES,
  stepsEnum.VARIABLES,
];

export const tiposPasosNoPuedeRetender = [
  tiposDePasosEnum.ALTA_AFECTADO,
  tiposDePasosEnum.ASIGNACION_CONSTANCIA,
  tiposDePasosEnum.CAMBIO_DE_PROPIETARIO,
  tiposDePasosEnum.CARGOS,
  tiposDePasosEnum.CITA,
  tiposDePasosEnum.FIRMA_ELECTRONICA,
  tiposDePasosEnum.SINCRONIZACION_DE_DATOS_FINANZAS,
  tiposDePasosEnum.VALIDACION_REQUISITOS,
  tiposDePasosEnum.AFECTAR_PADRON_JSON,
  tiposDePasosEnum.SUBDIVISION,
];

const ModalLog = ({
  visible,
  onCancel,
  callback,
  tramite,
  paso,
  pasos,
  puedeContinuar,
  pasosIniciales: _pasosIniciales,
  pasosVerificados: _pasosVerificados,
  historial,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const puede_continuar = Form.useWatch('puede_continuar', form);
  const comentario = Form.useWatch('comentario', form);
  const [formValues, pasoSiguiente] = generateValues(paso, pasos, puede_continuar);
  const pasosIniciales = _pasosIniciales
    .filter((e) => typeof e.key === 'string' && pasosInicialesPuedeReatender.includes(e.key));
  const pasoAfectar = pasos.find((p) => p.tipo_de_paso === tiposDePasosEnum.AFECTAR_PADRON_JSON);
  const afectado = historial.some((h) => h.paso === pasoAfectar?.id
    && pasoAfectar?.orden < pasos.find((p) => p.id === h.paso_a_mandar)?.orden);

  useEffect(() => {
    if (visible && paso) {
      form.setFieldsValue({ ...formValues, paso_a_mandar: puede_continuar ? [] : [paso.id] });
    }
  }, [visible, paso, puede_continuar]);

  useEffect(() => { form.setFieldsValue({ puede_continuar: puedeContinuar }); }, [puedeContinuar]);

  const _onCancel = () => {
    form.resetFields();
    onCancel();
  };

  const rules = { required: [{ required: true }] };

  const pasosAMandar = [
    ...(puede_continuar ? [] : pasosIniciales).map((e) => ({
      value: e.key,
      label: e.title,
    })),
    ...pasos.filter((p) => (puede_continuar ? p.orden > paso?.orden : p.orden <= paso?.orden))
      .map((p) => {
        const verified = _pasosVerificados.includes(p.id);
        const incompatible = !puede_continuar && tiposPasosNoPuedeRetender.includes(p.tipo_de_paso)
          && p.id !== paso?.id;
        const disabled = incompatible || (!puede_continuar && paso?.id === p.id)
          || (afectado && p.no_afectar_padron);
        const tooltip = (incompatible && 'Paso incompatible. No es posible realizar nuevamente.')
          || (afectado && p.no_afectar_padron && 'Padrón ya fue afectado');
        return {
          value: p.id,
          label: (
            <>
              {p.nombre}
              {' '}
              {verified && <CheckCircleOutlined />}
            </>
          ),
          disabled,
          tooltip,
        };
      }),
  ];

  const _pasos = [
    ..._pasosIniciales.map((e) => e.key),
    ...pasos.filter((p) => p.orden <= paso?.orden).map((e) => e.id),
  ];

  const onFinish = async () => {
    setLoading(true);
    const { archivos, paso_a_mandar, ...values } = form.getFieldsValue();
    const pasosVerificados = puede_continuar
      ? _pasos : _pasos.filter((e) => !paso_a_mandar.includes(e));
    const comentarios_del_tramite = JSON.stringify({
      pasoVerifico: values.paso,
      pasosVerificados,
    });
    values.paso_a_mandar = puede_continuar ? paso_a_mandar : paso_a_mandar.find((e) => typeof e === 'number');
    values.tramite = tramite;
    values.archivos = archivos?.fileList;
    const log = await postHistorialDeTramite(values, paso_a_mandar);
    if (log) {
      const _tramite = await patchTramite(tramite, { comentarios_del_tramite });
      callback(puede_continuar ? log.paso_a_mandar
        : paso_a_mandar[0], _tramite?.comentarios_del_tramite);
      onCancel();
    }
    setLoading(false);
  };

  return (
    <Modal
      keyboard
      footer={null}
      closable={false}
      visible={visible}
      title={(
        <FormSubmitControls
          label="Historial de seguimiento"
          onFinish={form.submit}
          onCancel={_onCancel}
          loading={loading}
        />
      )}
      onCancel={_onCancel}
      destroyOnClose
    >
      <Spin spinning={loading} tip="Loading...">
        <Row>
          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              name="cancelar_tramite"
              onFinish={() => onFinish()}
              initialValues={{
                puede_continuar: true,
                es_comentario_interno: true,
              }}
              onValuesChange={(changedValues) => {
                if ('comentario' in changedValues && !changedValues.comentario) {
                  form.resetFields(['archivos', 'es_comentario_interno']);
                }
              }}
            >
              <Col span={24}>
                <Form.Item
                  label="Paso actual"
                  name="paso"
                  rules={rules.required}
                >
                  <Select dataSource={pasos} disabled />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="¿Puede continuar?"
                  name="puede_continuar"
                  rules={rules.required}
                >
                  <Select
                    trueFalse
                    disabled={!puedeContinuar}
                    onChange={(val) => {
                      if (!val) form.setFieldsValue({ paso_a_mandar: [paso.id] });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label={puede_continuar ? 'Paso A Mandar' : 'Paso(s) A Atender Nuevamente'}
                  name="paso_a_mandar"
                  rules={rules.required}
                  className="no-capitalize"
                >
                  <Select
                    dataSource={pasosAMandar}
                    labelProp="label"
                    valueProp="value"
                    keyProp="value"
                    keyLabelRender={process.env.NODE_ENV === 'development'}
                    mode={!puede_continuar ? 'multiple' : undefined}
                    onChange={(selected) => {
                      if (puede_continuar) return;
                      form.setFieldsValue({
                        paso_a_mandar: pasosAMandar
                          .filter((e) => selected?.includes(e.value)).map((e) => e.value),
                      });
                    }}
                    allowClear={false}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Comentarios"
                  name="comentario"
                >
                  <Input.TextArea autoSize={{ minRows: 3, maxRows: 3 }} allowClear />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="¿Es comentario interno?"
                  name="es_comentario_interno"
                  rules={rules.required}
                  hidden={!pasoSiguiente}
                >
                  <Select trueFalse disabled={!comentario} />
                </Form.Item>
              </Col>
              <Dragger
                name="archivos"
                label="Archivos"
                limitMB={20}
                allowedExtensions={['pdf', 'png', 'jpg', 'jpeg']}
                disabled={!comentario}
              // multiple
              />
            </Form>
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
};

ModalLog.propTypes = {
  visible: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  callback: PropTypes.func.isRequired,
  tramite: PropTypes.number,
  paso: PropTypes.shape({
    id: PropTypes.number,
    orden: PropTypes.number,
  }),
  pasos: PropTypes.arrayOf(PropTypes.shape()),
  historial: PropTypes.arrayOf(PropTypes.shape()),
  pasosIniciales: PropTypes.arrayOf(PropTypes.shape()),
  pasosVerificados: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  puedeContinuar: PropTypes.bool,
};
ModalLog.defaultProps = {
  visible: false,
  tramite: null,
  paso: null,
  pasos: [],
  puedeContinuar: true,
  pasosIniciales: [],
  pasosVerificados: [],
  historial: [],
};

export default ModalLog;
